// src/features/mech/casting/questions/MaterialGradeCastingQuestion.jsx
import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Box } from "@mui/material";
import PerlLabelMedium from "../../../../shared/components/PerlLabelMedium";
import PerlTextField from "../../../../shared/components/PerlTextField";
import PerlChip from "../../../../shared/components/PerlChip";
import { apiFetch } from "../../../../api/apiClient";

// Material type arrays from MaterialSelectionQuestion
const PP_MATERIAL_TYPES = [
  "PP",
  "PP Copolymer",
  "PP Homopolymer",
  "PP+10%GB",
  "PP+10%GF",
  "PP+20%GF",
  "PP+GF",
  "PPA+16%GF",
  "PPA+33%GF",
  "PPA+35%GF",
  "PPA+45%GF",
  "PPE",
  "PPE+PPO",
  "PPE+PS",
  "PPO",
  "PPS+20%GF",
  "PPS+40%GF",
  "PPS+PTFE",
  "PPSU",
];

const PBT_MATERIAL_TYPES = [
  "PBT",
  "PBT+10%GF",
  "PBT+12%GF",
  "PBT+17%GF",
  "PBT+20%GF",
  "PBT+30%GF",
  "PBT+40%GF/MF",
  "PBT+PET",
  "PC+PBT",
  "PC+PBT 30%GF",
];

const PA_MATERIAL_TYPES = [
  "PA+Carbon Fiber",
  "PA11",
  "PA11+30%GF",
  "PA12",
  "PA12+50%GF",
  "PA46",
  "PA6",
  "PA6/12",
  "PA6/6T",
  "PA6+10% Kelvar+5% Teflon",
  "PA6+10%PTFE",
  "PA6+20%Carbon+15%teflon",
  "PA6+30%GF",
  "PA6+33%GF",
  "PA6+50%GF",
  "PA612",
  "PA66",
  "PA66 + 13% GF",
  "PA66 + 15%GF + 10% PTFE",
  "PA66 + 20% PTFE",
  "PA66 + 25% GF",
  "PA66 + 30%GF + 15% PTFE",
  "PA66 + 33% GF",
  "PA66 + 35% GF",
  "PA66 + 40%MF",
  "PA66 + 50% GF",
  "PPA+16%GF",
  "PPA+33%GF",
  "PPA+35%GF",
  "PPA+45%GF",
];

const LDPE_MATERIAL_TYPES = ["LLDPE"];

const HDPE_MATERIAL_TYPES = ["HDPE"];

const PVC_MATERIAL_TYPES = ["PVC Paste", "PMMA+PVC", "CPVC", "PVC Powder"];

/**
 * MaterialGradePlasticQuestion Component
 *
 * Custom question for Mechanical Plastics: captures the material grade/variant
 * Should be shown after material type is validated and valid
 *
 * @param {string} value - Current material grade value
 * @param {string} materialType - The material type (required for validation)
 * @param {function} onChange - Callback when the grade changes (receives string grade)
 * @param {boolean} [isMaterialValid] - Whether the material type is valid
 * @param {boolean} [isValidatingMaterial] - Whether material validation is in progress
 * @param {function} [onValidationChange] - Callback when validation state changes (receives {isValid, isGradeValid, isValidating})
 * @param {string} [error] - Optional error message
 * @param {string} [success] - Optional success/help message
 * @param {function} [onNextStep] - Optional callback to proceed to next step on Enter key
 * @param {number} [currentStep] - Current step index
 */
const MaterialGradeCastingQuestion = forwardRef(({ value, materialType, onChange, isMaterialValid = false, isValidatingMaterial = false, onValidationChange, error, success, onNextStep, currentStep }, ref) => {
  const [materialGrade, setMaterialGrade] = useState(value || "");
  const [gradeValidationMessage, setGradeValidationMessage] = useState("");
  const [isValidatingGrade, setIsValidatingGrade] = useState(false);
  const [isGradeValid, setIsGradeValid] = useState(true); // Default to true to avoid showing error initially

  // Sync grade state when value prop changes
  useEffect(() => {
    setMaterialGrade(value || "");
  }, [value]);

  // Validate grade via API
  const validateGrade = React.useCallback(async (material, grade) => {
    if (!grade || grade.trim() === "") {
      setGradeValidationMessage("");
      setIsGradeValid(true); // Empty grade is valid (optional field)
      if (onValidationChange) {
        onValidationChange({ isValid: true, isGradeValid: true, isValidating: false });
      }
      return true;
    }

    if (!material || material.trim() === "") {
      return true; // Don't validate grade if material is not set
    }

    setIsValidatingGrade(true);
    if (onValidationChange) {
      onValidationChange({ isValid: false, isGradeValid: false, isValidating: true });
    }

    try {
      const response = await apiFetch("/api/mech/validate-material-type", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          materialType: material,
          materialGrade: grade,
          materialCategory: "casting", // Specify plastics category
        }),
      });

      if (response.ok) {
        const result = await response.json();
        let message = result.message || "Not a valid material/grade for casting";
        
        const isValid = result.is_grade_valid !== false; // Default to true if undefined/null
        setGradeValidationMessage(message);
        setIsGradeValid(isValid);
        
        if (onValidationChange) {
          onValidationChange({ isValid, isGradeValid: isValid, isValidating: false });
        }
        return isValid;
      } else {
        setGradeValidationMessage("");
        setIsGradeValid(true); // Default to valid on API failure
        if (onValidationChange) {
          onValidationChange({ isValid: true, isGradeValid: true, isValidating: false });
        }
        return true;
      }
    } catch (err) {
      console.error("Grade validation error:", err);
      setGradeValidationMessage("");
      setIsGradeValid(true); // Default to valid on API failure
      if (onValidationChange) {
        onValidationChange({ isValid: true, isGradeValid: true, isValidating: false });
      }
      return true;
    } finally {
      setIsValidatingGrade(false);
    }
  }, [onValidationChange]);

  // Reset grade validation state when grade changes
  useEffect(() => {
    if (!materialGrade || materialGrade.trim() === "") {
      setGradeValidationMessage("");
      setIsGradeValid(true);
      if (onValidationChange) {
        onValidationChange({ isValid: true, isGradeValid: true, isValidating: false });
      }
    }
  }, [materialGrade, onValidationChange]);

  const handleMaterialGradeChange = (newGrade) => {
    setMaterialGrade(newGrade);
    onChange(newGrade);
  };

  // Handle Enter key on material grade field
  const handleMaterialGradeKeyDown = (e) => {
    if (e.key === "Enter" && onNextStep && materialGrade && materialGrade.trim() !== "") {
      e.preventDefault();
      onNextStep();
    }
  };

  // Check if material type is PP (Polypropylene)
  const isPP = React.useMemo(() => {
    if (!materialType) return false;
    const materialUpper = materialType.toUpperCase().trim();
    return (
      materialUpper.startsWith("PP") &&
      !materialUpper.startsWith("PPA") &&
      !materialUpper.startsWith("PPE") &&
      !materialUpper.startsWith("PPO") &&
      !materialUpper.startsWith("PPS")
    );
  }, [materialType]);

  // Check if material type is PBT
  const isPBT = React.useMemo(() => {
    if (!materialType) return false;
    const materialUpper = materialType.toUpperCase().trim();
    return materialUpper.startsWith("PBT");
  }, [materialType]);

  // Check if material type is PA (Polyamide/Nylon)
  const isPA = React.useMemo(() => {
    if (!materialType) return false;
    const materialUpper = materialType.toUpperCase().trim();
    return materialUpper.startsWith("PA");
  }, [materialType]);

  // Check if material type is LDPE
  const isLDPE = React.useMemo(() => {
    if (!materialType) return false;
    const materialUpper = materialType.toUpperCase().trim();
    return materialUpper === "LDPE" || materialUpper === "LLDPE";
  }, [materialType]);

  // Check if material type is HDPE
  const isHDPE = React.useMemo(() => {
    if (!materialType) return false;
    const materialUpper = materialType.toUpperCase().trim();
    return materialUpper === "HDPE";
  }, [materialType]);

  // Check if material type is PVC
  const isPVC = React.useMemo(() => {
    if (!materialType) return false;
    const materialUpper = materialType.toUpperCase().trim();
    return materialUpper.startsWith("PVC");
  }, [materialType]);

  // Handle chip selection
  const handleGradeChipClick = (grade) => {
    handleMaterialGradeChange(grade);
  };

  // Expose validation function for parent to call
  useImperativeHandle(ref, () => ({
    validate: async () => {
      if (!materialType || materialType.trim() === "") {
        return true; // Can't validate without material type
      }
      // Validate grade when called explicitly (e.g., from next button)
      return await validateGrade(materialType, materialGrade);
    },
    isValid: () => isGradeValid && !isValidatingGrade,
    isGradeValid: () => isGradeValid,
    isValidating: () => isValidatingGrade,
  }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <PerlLabelMedium sx={{ mb: 1, display: "block" }}>Enter Material Grade:</PerlLabelMedium>

      <PerlTextField
        label="Enter Material Grade"
        placeholder="e.g., EN‑GJL, ASTM A48, EN‑GJS, etc."
        value={materialGrade}
        onChange={(e) => {
          handleMaterialGradeChange(e.target.value);
        }}
        onKeyDown={handleMaterialGradeKeyDown}
        error={Boolean(error) || (gradeValidationMessage && !isValidatingGrade && !isGradeValid)}
        helperText={
          error || 
          (isValidatingGrade ? "Validating..." : "") ||
          (gradeValidationMessage || "")
        }
        fullWidth
      />

      {/* Show PP material type chips if material is PP and validated */}
      {isPP && isMaterialValid && !isValidatingMaterial && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          <PerlLabelMedium sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 0.5 }}>
            Common PP (Polypropylene) Variants:
          </PerlLabelMedium>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {PP_MATERIAL_TYPES.map((type) => (
              <Box key={type} sx={{ width: "auto", flexShrink: 0 }}>
                <PerlChip
                  label={type}
                  selected={materialGrade === type}
                  onClick={() => handleGradeChipClick(type)}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Show PBT material type chips if material is PBT and validated */}
      {isPBT && isMaterialValid && !isValidatingMaterial && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          <PerlLabelMedium sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 0.5 }}>
            Common PBT Variants:
          </PerlLabelMedium>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {PBT_MATERIAL_TYPES.map((type) => (
              <Box key={type} sx={{ width: "auto", flexShrink: 0 }}>
                <PerlChip
                  label={type}
                  selected={materialGrade === type}
                  onClick={() => handleGradeChipClick(type)}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Show PA material type chips if material is PA and validated */}
      {isPA && isMaterialValid && !isValidatingMaterial && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          <PerlLabelMedium sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 0.5 }}>
            Common PA (Polyamide/Nylon) Variants:
          </PerlLabelMedium>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {PA_MATERIAL_TYPES.map((type) => (
              <Box key={type} sx={{ width: "auto", flexShrink: 0 }}>
                <PerlChip
                  label={type}
                  selected={materialGrade === type}
                  onClick={() => handleGradeChipClick(type)}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Show LDPE material type chips if material is LDPE and validated */}
      {isLDPE && isMaterialValid && !isValidatingMaterial && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          <PerlLabelMedium sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 0.5 }}>
            Common LDPE Variants:
          </PerlLabelMedium>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {LDPE_MATERIAL_TYPES.map((type) => (
              <Box key={type} sx={{ width: "auto", flexShrink: 0 }}>
                <PerlChip
                  label={type}
                  selected={materialGrade === type}
                  onClick={() => handleGradeChipClick(type)}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Show HDPE material type chips if material is HDPE and validated */}
      {isHDPE && isMaterialValid && !isValidatingMaterial && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          <PerlLabelMedium sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 0.5 }}>
            Common HDPE Variants:
          </PerlLabelMedium>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {HDPE_MATERIAL_TYPES.map((type) => (
              <Box key={type} sx={{ width: "auto", flexShrink: 0 }}>
                <PerlChip
                  label={type}
                  selected={materialGrade === type}
                  onClick={() => handleGradeChipClick(type)}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Show PVC material type chips if material is PVC and validated */}
      {isPVC && isMaterialValid && !isValidatingMaterial && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          <PerlLabelMedium sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 0.5 }}>
            Common PVC Variants:
          </PerlLabelMedium>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {PVC_MATERIAL_TYPES.map((type) => (
              <Box key={type} sx={{ width: "auto", flexShrink: 0 }}>
                <PerlChip
                  label={type}
                  selected={materialGrade === type}
                  onClick={() => handleGradeChipClick(type)}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {success && (
        <PerlLabelMedium sx={{ mt: 1, color: "success.main" }}>{success}</PerlLabelMedium>
      )}
    </Box>
  );
});

MaterialGradeCastingQuestion.displayName = "MaterialGradeCastingQuestion";

export default MaterialGradeCastingQuestion;
